import { CloudLightning, CloudSun, Compass, Droplets, Eye, Gauge, Thermometer } from 'lucide-react'
import { Divider, Metric } from '../ui/Panels'
import { StateBadge, StateDot } from '../ui/State'
import { fmtAge } from '../../lib/format'
import {
  FLIGHT_CATEGORY_LABEL,
  FLIGHT_CATEGORY_TONE,
  formatClouds,
  formatVisibility,
  formatWind,
} from '../../types'
import type { WeatherSnapshot } from '../../types'

/* ============================================================================
 * ПАНЕЛЬ МЕТЕО (задача №10)
 *
 * Что показываем и — главное — что НЕ показываем:
 *
 *   • время НАБЛЮДЕНИЯ (observed_at), а не время получения ответа. Метрология
 *     без времени наблюдения — это мусор: METAR трёхчасовой давности и свежий
 *     METAR выглядят в данных одинаково.
 *   • признак устаревания (stale) — метка, а не просто серый текст.
 *   • источник данных — чтобы диспетчер знал, откуда цифры.
 *
 * Чего виджет НЕ делает принципиально:
 *   • не заменяет «нет данных» на нули;
 *   • не показывает осадки в мм/ч, потому что METAR их не содержит
 *     (showers_known === false — это «неизвестно», а не «осадков нет»);
 *   • не прячет причину отсутствия данных — она в reasons[].
 *
 * Ветка «данных нет» и ветка «данные протухли» разведены осознанно:
 * в первом случае виноват источник, во втором — канал связи, и лечатся
 * они по-разному.
 * ========================================================================= */

export function WeatherPanel({
  weather,
  onRefresh,
  busy,
}: {
  weather: WeatherSnapshot | null
  onRefresh: () => void
  busy?: boolean
}) {
  // --- Нет данных вообще -------------------------------------------------
  if (!weather) {
    return (
      <div className="space-y-2 p-2.5">
        <div className="rounded-md border border-line px-2.5 py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold">
              <CloudSun size={12} aria-hidden />
              Метео
            </div>
            <StateBadge state="pending">загрузка…</StateBadge>
          </div>
        </div>
      </div>
    )
  }

  const { reasons, stale } = weather
  const hasData = weather.icao !== null && weather.metar !== null

  // Категория полёта — главный для диспетчера вывод, поэтому она в шапке.
  const category = weather.flight_category ?? 'UNKNOWN'
  const tone = stale
    ? ('warning' as const)
    : FLIGHT_CATEGORY_TONE[category] ?? ('pending' as const)

  const headerState = !hasData
    ? ('critical-error' as const)
    : stale
      ? ('warning' as const)
      : tone

  const headerLabel = !hasData
    ? 'Нет данных'
    : stale
      ? 'Устарело'
      : FLIGHT_CATEGORY_LABEL[category] ?? category

  return (
    <div className="space-y-2 p-2.5">
      {/* Шапка: станция, категория полёта, свежесть */}
      <div className={`rounded-md border px-2.5 py-2 ut-stated ut-state-${headerState}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold">
            <CloudSun size={12} aria-hidden />
            Метео
            {weather.icao && (
              <span className="font-mono text-[10px] text-ink-muted">
                {weather.icao}
              </span>
            )}
          </div>
          <StateBadge state={headerState}>
            <StateDot state={headerState} /> {headerLabel}
          </StateBadge>
        </div>

        {/* Время наблюдения — обязательно. Без него цифры бесполезны. */}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-ink-muted">
          <span title="Время наблюдения (UTC), а не время получения">
            наблюдение:{' '}
            <b className="text-ink">
              {weather.observed_at ? fmtAge(weather.observed_at) : '—'}
            </b>
          </span>
          {weather.age_min !== null && weather.age_min !== undefined && (
            <span title="Сколько минут назад наблюдение">
              возраст {weather.age_min} мин
            </span>
          )}
          {weather.source && <span title="Источник данных">↯ {weather.source}</span>}
        </div>
      </div>

      {/* Причины отсутствия/устаревания — не прячем */}
      {reasons.length > 0 && (
        <div className="rounded-md border border-line/70 bg-ink/5 px-2.5 py-1.5">
          <ul className="space-y-0.5 text-[10px] text-ink-muted">
            {reasons.map((r) => (
              <li key={r}>• {r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Нет данных — дальше показывать нечего */}
      {!hasData ? (
        <div className="px-1 py-3 text-center text-[11px] text-ink-muted">
          Данных нет. Источник: {weather.source ?? 'неизвестен'}.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-1.5">
            <Metric
              label="Температура"
              value={weather.temp_c === null ? '—' : Math.round(weather.temp_c)}
              unit="°C"
              icon={Thermometer}
              title={
                weather.dewpoint_c === null
                  ? 'точка росы неизвестна'
                  : `точка росы ${weather.dewpoint_c} °C`
              }
            />
            <Metric
              label="Ветер"
              value={formatWind(
                weather.wind_dir_deg,
                weather.wind_speed_kt,
                weather.gust_observed,
              )}
              icon={Compass}
              title={
                weather.wind_dir_deg === null && (weather.wind_speed_kt ?? 0) > 0
                  ? 'Переменный ветер (VRB) — фиксированного направления нет'
                  : 'Направление и скорость ветра'
              }
            />
            <Metric
              label="Видимость"
              value={formatVisibility(weather.visibility_m)}
              icon={Eye}
              title={
                weather.visibility_m === null
                  ? 'Видимость менее 1 км (METAR: M) — точное значение неизвестно'
                  : weather.cavok
                    ? 'CAVOK: видимость 10 км и более, явлений нет'
                    : 'Дальность видимости'
              }
            />
            <Metric
              label="Давление"
              value={weather.pressure_hpa === null ? '—' : weather.pressure_hpa}
              unit="гПа"
              icon={Gauge}
              title="Атмосферное давление на уровне станции (QNH)"
            />
          </div>

          <Divider label="Облачность и осадки" />

          <div className="space-y-1.5 text-[11px]">
            <div className="flex items-center justify-between gap-2">
              <span className="text-ink-muted">Облачность</span>
              <span className="font-mono">
                {formatClouds(weather.clouds, weather.cavok)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-ink-muted">Потолок</span>
              <span className="font-mono">
                {weather.ceiling_ft === null
                  ? '—'
                  : `${weather.ceiling_ft} фут`}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1 text-ink-muted">
                <Droplets size={11} aria-hidden />
                Осадки
              </span>
              <span className="font-mono">
                {weather.showers_known ? 'известны' : 'не измеряются'}
              </span>
            </div>
          </div>

          {!weather.showers_known && (
            <p className="text-[10px] text-ink-muted">
              METAR не содержит интенсивности осадков в мм/ч — «не измеряются»
              означает «неизвестно», а не «осадков нет».
            </p>
          )}
        </>
      )}

      <Divider />

      {/* Первоисточник — диспетчер может сверить сам */}
      {weather.metar && (
        <div className="space-y-0.5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-muted">
            METAR
          </span>
          <p className="break-all font-mono text-[10px] text-ink-muted">
            {weather.metar}
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={onRefresh}
        disabled={busy}
        className="ut-btn w-full text-[11px] disabled:opacity-50"
      >
        {busy ? 'Обновление…' : 'Обновить'}
      </button>
    </div>
  )
}

/**
 * Компактная строка для верхней панели: видно даже на свёрнутых панелях.
 * Показывает ровно то, что нужно за секунду: категорию, ветер, видимость.
 */
export function WeatherStrip({ weather }: { weather: WeatherSnapshot | null }) {
  if (!weather || weather.icao === null) {
    return (
      <span className="flex items-center gap-1 border border-line/50 px-1.5 py-0.5 text-[10px] text-ink-muted/60">
        метео: нет данных
      </span>
    )
  }
  const category = weather.flight_category ?? 'UNKNOWN'
  const tone = weather.stale
    ? ('warning' as const)
    : FLIGHT_CATEGORY_TONE[category] ?? ('pending' as const)

  return (
    <span
      className="flex items-center gap-1.5 border border-line/70 px-1.5 py-0.5 text-[10px]"
      title={`${weather.icao}: ${weather.metar ?? 'нет METAR'}`}
    >
      <CloudLightning size={10} aria-hidden />
      <StateDot state={tone} />
      <b>{FLIGHT_CATEGORY_LABEL[category] ?? category}</b>
      <span className="text-ink-muted">
        {formatWind(weather.wind_dir_deg, weather.wind_speed_kt, null)}
      </span>
      <span className="text-ink-muted">
        {formatVisibility(weather.visibility_m)}
      </span>
      {weather.stale && <span className="text-warning">уст.</span>}
    </span>
  )
}